import { Group, Stack, Text, useMantineTheme } from '@mantine/core';
import { IconAlertOctagon, IconAlertTriangle, IconCalendarDue, IconCircleCheck } from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { Milestone } from '../../types/milestones';
import {
  formatMilestonePastDueLabel,
  getMilestoneDaysPastDue,
  getMilestonePastDueBadgeStatus,
} from '../../utils/milestonePastDue';

const DATE_FORMAT = 'MM/DD/YYYY';
/** Height of one secondary date row + stack gap — used to keep card heights even. */
export const MILESTONE_SECONDARY_DATE_ROW_OFFSET = 20;

function formatMilestoneDate(value: string | undefined): string | null {
  if (!value?.trim()) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(DATE_FORMAT) : null;
}

/** True when Past Due or completed date is shown under the due date. */
export function milestoneHasSecondaryDateLabel(milestone: Milestone): boolean {
  if (getMilestonePastDueBadgeStatus(milestone.status, milestone.due_date)) {
    return true;
  }
  if (milestone.status === 'completed' && formatMilestoneDate(milestone.completed_date)) {
    return true;
  }
  return false;
}

export function MilestoneDateLabels({ milestone }: { milestone: Milestone }) {
  const theme = useMantineTheme();
  const dueDateLabel = formatMilestoneDate(milestone.due_date);
  const completedDateLabel =
    milestone.status === 'completed' ? formatMilestoneDate(milestone.completed_date) : null;
  const pastDueStatus = getMilestonePastDueBadgeStatus(milestone.status, milestone.due_date);
  const daysPastDue = getMilestoneDaysPastDue(milestone.status, milestone.due_date);
  const pastDueLabel =
    daysPastDue != null ? formatMilestonePastDueLabel(daysPastDue) : 'Past Due';
  const pastDueColor =
    pastDueStatus === 'past_due_grace'
      ? theme.colors.yellow[6]
      : pastDueStatus === 'past_due'
        ? theme.colors.red[6]
        : null;
  const PastDueIcon = pastDueStatus === 'past_due_grace' ? IconAlertTriangle : IconAlertOctagon;

  return (
    <Stack spacing={4}>
      {dueDateLabel ? (
        <Group spacing={6} noWrap aria-label={`Due ${dueDateLabel}`}>
          <IconCalendarDue
            size={16}
            style={{ color: 'var(--mantine-color-dimmed)' }}
            aria-hidden
          />
          <Text size="xs" color="dimmed" weight={600}>
            {dueDateLabel}
          </Text>
        </Group>
      ) : null}
      {pastDueStatus && pastDueColor ? (
        <Group spacing={6} noWrap aria-label={pastDueLabel}>
          <PastDueIcon size={16} style={{ color: pastDueColor, display: 'block' }} aria-hidden />
          <Text size="xs" weight={600} style={{ color: pastDueColor }}>
            {pastDueLabel}
          </Text>
        </Group>
      ) : null}
      {completedDateLabel ? (
        <Group spacing={6} noWrap aria-label={`Completed ${completedDateLabel}`}>
          <IconCircleCheck
            size={16}
            style={{ color: 'var(--mantine-color-dimmed)' }}
            aria-hidden
          />
          <Text size="xs" color="dimmed" weight={600}>
            {completedDateLabel}
          </Text>
        </Group>
      ) : null}
    </Stack>
  );
}
